// Topic landing pages: the ideas that carry this product, written against what
// it actually does.
//
// The category is full of promises nobody can keep — agents that negotiate
// thousands of contracts unattended, deals closed in thirty minutes, blockchain
// identity, zero-knowledge proofs. Repeating them would win a click and lose the
// meeting, because the first demo would contradict the page.
//
// So every page here follows the same discipline: name the real shift, say
// plainly what works today, and say just as plainly where the limit is. In a
// field this over-sold, being the one page a CTO can check is the differentiator.
//
// `reality` is the section that keeps this honest. It must reflect
// feature-status.ts; if a capability moves there, it moves here.

export type Topic = {
  slug: string;
  /** Nav + hero title. */
  name: string;
  metaTitle: string;
  metaDescription: string;
  /** One line under the H1. */
  heroLead: string;
  /** The shift being described, 2-3 paragraphs. */
  thesis: string[];
  /** What this looks like in practice, concrete. */
  inPractice: { title: string; body: string }[];
  /** What is true today vs. what is not. The credibility of the whole page. */
  reality: { works: string[]; doesNot: string[] };
  /** Search intents this page answers. */
  queries: string[];
  faqs: { q: string; a: string }[];
};

export const TOPICS: Topic[] = [
  {
    slug: "agentic-ai-b2b",
    name: "Agentic AI in B2B",
    metaTitle: "Agentic AI for B2B: what actually works in 2026 | Reality Twin",
    metaDescription:
      "Agentic AI promises autonomous business agents. Here is what genuinely works today, what does not, and how to adopt it without betting the company on it.",
    heroLead:
      "The category is loud. This page is the part a CTO can verify — including the limits.",
    thesis: [
      "Agentic AI describes software that does not wait to be operated. It holds a goal, takes steps toward it and reports back. In B2B that is genuinely new: the previous generation of tools organised information for a human who then did the work.",
      "Most of what is written about it, though, describes a system nobody has shipped: agents negotiating thousands of contracts unattended, closing deals in half an hour, settling terms without anyone reading them. That is not caution about the technology — it is a description of software that does not exist yet, and buying against it is how pilots die.",
      "The useful question is narrower and answerable: which parts of a commercial conversation can an agent hold today, with what evidence, and where must a person still decide? That is what this page answers, and it is the line the product is built on.",
    ],
    inPractice: [
      {
        title: "It answers instead of collecting an email",
        body: "A visitor asks something specific at 23:00. The twin answers from your own indexed material and names its sources, or says it does not know and offers a person. That is an agent doing the first half of a sales conversation, and it is fully live today.",
      },
      {
        title: "It looks for a reason before it interrupts you",
        body: "Matching does not rank companies by keyword similarity. It scores seven named signals, then a verification pass has to quote the counterpart's own text to justify the match. If it cannot, the match is never shown. Most candidates are rejected — that is the intended outcome.",
      },
      {
        title: "It prepares; you decide",
        body: "Two twins can hold a structured meeting with an objective you set, and the transcript comes back with an analyst verdict that has to cite what was actually said. Nothing about it binds you. The contract that follows is signed by a named person, not by an agent.",
      },
    ],
    reality: {
      works: [
        "Grounded answering with sources, and an honest 'I don't know' with human handoff",
        "Verified matching with evidence, where an unjustifiable match is not shown at all",
        "Twin-to-twin meetings with an objective, a transcript and a cited verdict",
        "Contracts and deals signed electronically, with the terms frozen by hash",
      ],
      doesNot: [
        "Agents do not agree prices or terms on your behalf. Binding statements are off unless you switch them on, and even then a person signs.",
        "There is no autonomous purchasing, no spending limit an agent may use, and no automatic counter-offer loop.",
        "Nothing is connected to SAP or Salesforce. Knowledge comes from your website and uploaded documents.",
      ],
    },
    queries: [
      "agentic ai b2b",
      "autonomous ai agents for business",
      "ai agents that negotiate",
      "what is agentic ai in sales",
      "agentic ai use cases b2b",
    ],
    faqs: [
      {
        q: "Can the AI close a deal without me?",
        a: "No. It can answer, qualify, prepare and draft. Every contract is signed by a named person, and that signature records who signed, when, from which IP, and a SHA-256 fingerprint of the exact text they saw.",
      },
      {
        q: "What stops it from inventing capabilities we do not have?",
        a: "It answers from your indexed material and cites sources. It is instructed to say it does not know rather than guess. Every answer is logged in a statement register so you can check what it told people.",
      },
      {
        q: "How is this different from a chatbot?",
        a: "A chatbot answers on your site. This also publishes a machine-readable profile other companies' agents can query, and can hold a structured conversation with another company's twin. The difference is whether it is reachable by other software, not how well it chats.",
      },
    ],
  },

  {
    slug: "ai-to-ai-business",
    name: "AI-to-AI business",
    metaTitle: "When your AI talks to their AI: machine-to-machine B2B | Reality Twin",
    metaDescription:
      "Buyers now research through AI assistants. If your company is not machine-readable, you are not in the shortlist. How agent-to-agent discovery actually works.",
    heroLead: "Your next buyer may never load your website. Their assistant will.",
    thesis: [
      "A growing share of B2B research starts inside an assistant rather than a search box. The person asks for options; the model answers from what it can reach. If your company is only readable by a human with a browser, you are not in that answer.",
      "This is a distribution change, not a marketing one. It is closer to the arrival of search engines than to a new channel: being reachable by the software that now does the looking is either something you have or something you do not.",
      "What makes it tractable is that the plumbing already exists and is open. MCP lets an assistant call your twin directly. An Agent Card describes who you are in a form software can parse. llms.txt gives a model the text without the layout. None of it requires anyone to adopt a new standard you invented.",
    ],
    inPractice: [
      {
        title: "Reachable from inside ChatGPT and Claude",
        body: "The MCP server exposes discovery, your Agent Card, a question endpoint and lead submission. An assistant can find a company, read its verified profile, ask its twin a question and hand over contact details — without a human opening a browser.",
      },
      {
        title: "A profile written for software",
        body: "The Agent Card at /.well-known/agent.json states identity, verification status and how to transact. Every public twin also exposes one over HTTP. It is the difference between a model guessing what you do and reading it.",
      },
      {
        title: "Text without the interface",
        body: "llms.txt and llms-full.txt publish the substance of the site as plain text, so a model ingests what you actually say rather than a navigation menu. The sitemap carries hreflang for all seven languages.",
      },
    ],
    reality: {
      works: [
        "MCP server, reachable from assistants that support it",
        "Agent Card over HTTP for every public twin",
        "llms.txt, llms-full.txt, robots.txt and a multilingual sitemap",
        "Public twin profile and marketplace that other agents can search",
      ],
      doesNot: [
        "Being reachable does not guarantee a model will cite you. No one can promise placement inside someone else's model.",
        "WhatsApp, Slack and Teams are not connected yet.",
        "Agent-to-agent traffic analytics exist but are new — treat the numbers as directional.",
      ],
    },
    queries: [
      "ai agent discovery b2b",
      "mcp server for business",
      "how to be found by chatgpt",
      "agent card a2a",
      "llms.txt for companies",
    ],
    faqs: [
      {
        q: "Do I have to build anything for this?",
        a: "No. Publishing your twin creates the Agent Card, the MCP endpoint and the machine-readable text. What you do have to do is give the twin something worth reading.",
      },
      {
        q: "Can I see which agents queried me?",
        a: "Yes, agent traffic is recorded and shown in analytics. It is a young feature, so read it as a signal rather than an audited count.",
      },
      {
        q: "Is this just SEO with a new name?",
        a: "It overlaps but is not the same. SEO optimises a page for a ranking algorithm. This makes your company answerable by software — the model gets facts and sources instead of a page it has to interpret.",
      },
    ],
  },

  {
    slug: "trust-layer",
    name: "The trust layer",
    metaTitle: "Verified company identity for AI answers | Reality Twin",
    metaDescription:
      "An AI answer is only worth as much as the identity behind it. Domain verification, source-cited answers and a hash-anchored record of everything the twin said.",
    heroLead: "Anyone can put a confident chatbot on a website. The question is who is behind it.",
    thesis: [
      "The hardest problem in commercial AI is not fluency, it is accountability. A model that sounds certain and is wrong costs more than one that says nothing, because someone acts on it.",
      "In a network where agents talk to agents, that problem compounds: the receiving side cannot see a website, judge a logo or read a room. It has whatever the other party asserts. Without a verifiable identity underneath, the whole exchange is confident strangers quoting each other.",
      "So verification is not a badge here, it is the load-bearing part. A seal that can be obtained without proof is worse than no seal, because it launders a claim into something that looks checked.",
    ],
    inPractice: [
      {
        title: "Domain ownership, proven three ways",
        body: "A DNS TXT record, a meta tag, or a file at a well-known path. Whoever controls DNS often cannot edit the CMS and the reverse, so all three are first-class. The seal only appears once the token is actually found.",
      },
      {
        title: "Answers carry their sources",
        body: "The twin answers from indexed material and names where each claim came from. Where it has nothing, it is instructed to say so and offer a person rather than fill the gap.",
      },
      {
        title: "Commitment levels, off by default",
        body: "Nothing the twin says is binding unless you explicitly enable binding statements. While off, it gives indicative guidance and defers to your team for written confirmation — the liability guard sits on by default, not off.",
      },
      {
        title: "A statement register you can export",
        body: "Every answer given to the outside world is timestamped, source-attributed and hash-anchored. If a customer says 'your AI told me X', you can check whether it did.",
      },
    ],
    reality: {
      works: [
        "Domain verification via DNS, meta tag or file",
        "Source-cited answers with an honest 'I don't know'",
        "Commitment levels — binding statements require explicit opt-in",
        "Statement register, exportable as JSON",
        "Swiss and EU data protection posture with a DPA and named sub-processors",
      ],
      doesNot: [
        "Commercial-register (Zefix) numbers are recorded but not yet checked automatically against the registry.",
        "There is no cryptographic company identity or blockchain-anchored ID. Verification proves domain control, which is a narrower and honest claim.",
        "Sanctions and credit screening are not part of the product.",
      ],
    },
    queries: [
      "verified ai company profile",
      "prevent ai hallucination b2b",
      "ai answer with sources",
      "company domain verification ai",
      "ai liability business statements",
    ],
    faqs: [
      {
        q: "What does the verified seal actually prove?",
        a: "That whoever set up the twin controls the company's domain. That is a real, checkable fact. It is deliberately not presented as proof of the company's legal existence or solvency — those are different checks we do not perform.",
      },
      {
        q: "What happens if the twin says something wrong?",
        a: "The statement register lets you find exactly what was said, when, and on which sources. Binding commitments are off unless you enabled them, so an incorrect answer is a correction, not a contract.",
      },
      {
        q: "Where is our data?",
        a: "Reality Twin is a Swiss company, developed in Hinwil near Zurich and governed by Swiss law. The data itself is hosted in the European Union (Ireland), covered by the GDPR and the Swiss FADP, with a Data Processing Addendum available and every sub-processor listed publicly.",
      },
    ],
  },

  {
    slug: "end-of-cold-outreach",
    name: "The end of cold outreach",
    metaTitle: "Replace cold outreach with verified inbound matching | Reality Twin",
    metaDescription:
      "Cold email works less every year. Matching that has to prove why two companies should talk — with the evidence attached — is a better use of the same hour.",
    heroLead: "The problem with cold outreach was never the writing. It was the guessing.",
    thesis: [
      "Cold outreach is expensive because most of it is aimed at people with no reason to answer. The sequencing tools of the last decade made it cheaper to send, which made the average message worse and the average inbox angrier.",
      "The alternative is not more personalisation on top of the same guess. It is knowing, before you write anything, that the other side stated a need you can serve — and being able to show what they said.",
      "That reframes the work. Instead of a hundred messages hoping for two replies, you get a short list where each entry carries a quote from the counterpart's own text explaining why it is there. Fewer conversations, started further along.",
    ],
    inPractice: [
      {
        title: "A match has to justify itself",
        body: "Semantic retrieval finds candidates, seven named signals score them, then a verification pass must quote the counterpart's own words. No quote, no match. You see the reasoning, not just a percentage.",
      },
      {
        title: "You see who benefits",
        body: "Every match says whether it is mutual, whether they need you, or whether you need them — decided by the stated needs on each side, not by a blended score. A one-sided fit is labelled as one.",
      },
      {
        title: "The radar watches for change, not for rows",
        body: "Six named triggers: a counterpart wrote down a need you serve, a complementary listing appeared, a fitting company joined, a counterpart got verified, your own twin keeps failing the same question, or something you shortlisted has been sitting untouched.",
      },
      {
        title: "Dismissing is permanent",
        body: "Say no to a company once and it is excluded from future matching at the database level. A tool that keeps re-suggesting what you rejected is training you to ignore it.",
      },
    ],
    reality: {
      works: [
        "Verified matching with quoted evidence and a visible signal breakdown",
        "Six-trigger deal radar with urgency that decays per trigger type",
        "Outreach drafts written on request, referencing the concrete trigger",
        "Permanent dismissal that excludes a counterpart from future matching",
      ],
      doesNot: [
        "Matching cannot create counterparts. Its output is bounded by how many companies are in the network — an empty result on a small network is the honest one.",
        "Nothing is sent on your behalf. Drafts are written for you to read, edit and send.",
        "There is no email sequencing, no open tracking and no automated follow-up cadence.",
      ],
    },
    queries: [
      "alternative to cold email b2b",
      "ai lead matching",
      "b2b partner matching software",
      "intent based outreach",
      "stop cold calling b2b",
    ],
    faqs: [
      {
        q: "Does it send the emails for me?",
        a: "No. It writes a draft when you ask for one, referencing the specific reason the signal appeared. You send it. Nothing leaves your account automatically.",
      },
      {
        q: "Why do I sometimes get no matches at all?",
        a: "Because nothing cleared the bar. The engine will not manufacture a match to fill a page — it rejects candidates it cannot evidence, and it tells you how many companies were searchable when it looked.",
      },
      {
        q: "How is the score calculated?",
        a: "Seven weighted signals: stated need, what you cover for them, profile similarity, industry adjacency, market, counterpart trust and recent activity. You can open the breakdown on any match and see each one.",
      },
    ],
  },

  {
    slug: "ai-negotiation-legal",
    name: "Legal certainty",
    metaTitle: "Who is liable when an AI agrees something? | Reality Twin",
    metaDescription:
      "The question that decides adoption. How commitment levels, human signature and hash-frozen terms keep an AI-assisted deal defensible.",
    heroLead:
      "The blocker in most boardrooms is not the technology. It is who answers for what the AI said.",
    thesis: [
      "An AI agent is not a legal person. Under Swiss and EU law it is a tool, and a company is bound by what its tools declare in much the same way a bank is bound by its cash machine. That is a settled principle, and it is exactly why the guardrails matter more than the model.",
      "Most discussion of this jumps to smart contracts and automated price corridors. Useful eventually, but it skips the question a lawyer actually asks first: can you show what was said, to whom, on what basis, and who authorised it?",
      "That is an evidence problem before it is an automation problem — and it is solvable today, which is why this product solves that end first.",
    ],
    inPractice: [
      {
        title: "Binding is off until you turn it on",
        body: "By default the twin never promises a price, a date or a term. It gives indicative guidance and defers to your team. Nothing can be logged as binding unless the owner explicitly enabled it.",
      },
      {
        title: "A person signs, and the signature carries proof",
        body: "Contracts are signed by a named signatory with an explicit consent step. The server records the timestamp, IP address, browser and the signer's email — none of it accepted from the client.",
      },
      {
        title: "The text is frozen at signature",
        body: "The contract body is hashed with SHA-256 and the signature binds to that hash. The countersigning party sends back the hash it was shown, so nobody can sign a document other than the one they read, and terms cannot be edited from under a signature.",
      },
      {
        title: "Everything said is on the record",
        body: "The statement register keeps every answer the twin gave the outside world, timestamped and source-attributed, and exports as JSON for your files.",
      },
    ],
    reality: {
      works: [
        "Commitment levels with binding statements off by default",
        "Electronic signature with name, consent, timestamp, IP and user agent",
        "Terms frozen by SHA-256; countersignature verifies the same hash",
        "Statement register, exportable",
        "Swiss law and jurisdiction stated in the framework agreement",
      ],
      doesNot: [
        "This is a simple electronic signature, not a qualified one (QES). For Swiss and EU service contracts that is generally sufficient, but contracts requiring statutory form are out of scope.",
        "There are no automated price corridors or spending limits an agent may transact within.",
        "The contract templates are written for the platform's own use and have not been reviewed by external counsel. Have yours read them before you rely on them at scale.",
      ],
    },
    queries: [
      "ai contract liability",
      "who is liable ai agent contract",
      "electronic signature switzerland b2b",
      "ai negotiation legal risk",
      "binding ai statements company",
    ],
    faqs: [
      {
        q: "Is a signature made here legally valid?",
        a: "It is a simple electronic signature: explicit consent plus the signatory's typed name, with timestamp, IP and a hash of the exact text recorded. For service agreements under Swiss and EU law that is generally valid, since no particular form is prescribed. Contracts with a statutory form requirement need a qualified signature, which this is not.",
      },
      {
        q: "What if our AI says something we did not authorise?",
        a: "Binding statements are off unless you enabled them, so by default nothing it says is presented as a commitment. The statement register lets you show precisely what was said and on what sources.",
      },
      {
        q: "Can we set spending limits for the agent?",
        a: "Not today, and we would rather say so than imply otherwise. The agent prepares; a person signs. Automated limits only make sense once agents transact unattended, which is not where the technology honestly is.",
      },
    ],
  },

  {
    slug: "sme-market-access",
    name: "Reach without a sales team",
    metaTitle: "Global B2B reach for small companies | Reality Twin",
    metaDescription:
      "A ten-person company cannot staff a sales floor. It can be answerable, verifiable and discoverable around the clock — which is most of what a sales floor was for.",
    heroLead:
      "The gap between a small supplier and a large one was never the product. It was who could afford to be reachable.",
    thesis: [
      "For most of commercial history, market access was bought. Bigger companies won not because their product was better but because they could staff the phones, attend the fairs and answer at three in the morning in another timezone.",
      "That advantage is availability, and availability is the one thing software genuinely levels. A twin that answers accurately from your own material at 02:00 in seven languages is doing the part of the job that used to require headcount.",
      "It does not level everything, and claiming otherwise would be silly. Reputation, references and relationships still favour incumbents. But being findable and answerable is now separable from being large, and that is a real shift for a workshop with nine people and a specialism.",
    ],
    inPractice: [
      {
        title: "Answering at any hour, in seven languages",
        body: "The twin answers from your own indexed material in English, German, Spanish, French, Italian, Russian and Chinese — the enquiry that would otherwise have waited until Monday gets a grounded answer immediately.",
      },
      {
        title: "A public profile other agents can query",
        body: "Your twin is listed in the marketplace and exposes an Agent Card. A buyer's assistant can find and interrogate you the same way it would a company a hundred times your size.",
      },
      {
        title: "Say what you are looking for",
        body: "Stated needs are the strongest signal on the network. Writing down what you want makes you findable in the direction you actually care about, not just for what you already sell.",
      },
      {
        title: "Quote and get paid through the platform",
        body: "Service requests come in, you quote, the customer pays online, and what you are owed is recorded in a ledger with its status — not just mentioned in an email.",
      },
    ],
    reality: {
      works: [
        "Grounded answering in seven languages, around the clock",
        "Public profile, marketplace listing and Agent Card",
        "Business Exchange listings with nightly matching",
        "Paid service requests with a payout ledger you can check",
        "A free tier that is enough to validate whether any of this works for you",
      ],
      doesNot: [
        "Reach is bounded by network size. The exchange and matching get better as more companies join, and today that number is small — we would rather you knew that before signing up than after.",
        "Payouts are transferred by bank rather than instantly. The ledger shows what is owed and when it went out.",
        "No agency or reseller workspace yet for managing many twins.",
      ],
    },
    queries: [
      "b2b sales without sales team",
      "ai sales for small business",
      "global reach small company b2b",
      "ai twin for kmu",
      "24/7 sales answering b2b",
    ],
    faqs: [
      {
        q: "We are nine people. Is this realistic for us?",
        a: "The answering, the multilingual coverage and the public profile all work regardless of your size and are the parts that replace availability. The network effects — matching, exchange, twin meetings — depend on how many companies are on the platform, which is still small. Judge it on the first part today.",
      },
      {
        q: "How long does setup take?",
        a: "The twin builds itself from your website, then you add documents. The work is in what you feed it, not in configuration. A thin twin answers thinly, and the product will tell you where its knowledge has gaps.",
      },
      {
        q: "What does it cost to find out?",
        a: "There is a free tier. It is deliberately enough to see whether the twin answers your customers' real questions well, because that is the thing worth testing before you pay.",
      },
    ],
  },
];

export function getTopic(slug: string): Topic | undefined {
  return TOPICS.find((t) => t.slug === slug);
}
